import { Button, ButtonGroup } from "@components/button";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import SmartFormDeleteButton from "@components/smart/smartFormDeleteButton/SmartFormDeleteButton";
import { EntitySetName, IPrSubmissionEntity, PrSubmissionEntity } from "@odata/GeneratedEntityTypes";
import { PrSubmissionSubtypeCode, PrSubmissionTypeCode } from "@odata/GeneratedEnums";
import { loadAndStoreRanges, setMatchingNumberRange } from "@pages/numberRange/NumberRange.utils";
import ExportSubmissionDialog from "@pages/payroll/submission/ExportSubmissionDialog";
import { EXPORT_XML_ACTION_ID } from "@pages/payroll/submission/Submission.def";
import { exportToXML } from "@pages/payroll/submission/Submission.utils";
import { cloneDeep } from "lodash";
import React from "react";

import { WithPermissionContext } from "../../../contexts/permissionContext/withPermissionContext";
import { FormMode, Status, TextAlign } from "../../../enums";
import { EmptyObject } from "../../../global.types";
import { IContextInitArgs, IFormStorageDefaultCustomData } from "../../../views/formView/FormStorage";
import { FormViewForExtend, IFormViewProps } from "../../../views/formView/FormView";

interface ISubmissionFormCustomDate extends IFormStorageDefaultCustomData {
    isSubmitDialogOpen?: boolean;
}

export interface IProps<E extends IPrSubmissionEntity> extends IFormViewProps<E, ISubmissionFormCustomDate>, WithPermissionContext {
    buttons?: React.ReactElement;
}

abstract class SubmissionsFormView<E extends IPrSubmissionEntity> extends FormViewForExtend<E, IProps<E>> {
    abstract typeCode: PrSubmissionTypeCode;
    abstract subTypeCode: PrSubmissionSubtypeCode;
    abstract pathToEmployee: string;

    typeSpecificOnAfterLoad(isNew: boolean): Promise<void> {

        const { entity } = this;
        if (entity?.PodaniValidationErrors?.length) {
            const { storage } = this.props;
            const errorMessages = entity.PodaniValidationErrors.map(e => e.Message);

            errorMessages.unshift(storage.t("Submission:Form.ValidationStatusWarningMessage"));
            this.infoAlert = {
                title: storage.t("Common:Warnings.Warning"),
                subTitle: errorMessages,
                hideSubtitleDiv: true,
                status: Status.Warning
            };
        } else {
            this.infoAlert = null;
        }

        return Promise.resolve();
    }

    constructor(props: IProps<E>) {
        super(props);

        this.handleChange = this.handleChange.bind(this);
        this.onAfterLoad = this.onAfterLoad.bind(this);
    }

    componentDidMount(): void {
        super.componentDidMount();
        this.registerHeader();
    }

    componentDidUpdate(prevProps: Readonly<IProps<E>>, prevState: Readonly<EmptyObject>): void {
        super.componentDidUpdate(prevProps, prevState);
        this.registerHeader();
    }

    registerHeader = (): void => {
        const { storage } = this.props;
        if (storage.data.bindingContext) {
            const numberOursBc = storage.data.bindingContext.navigate(PrSubmissionEntity.NumberOurs);
            storage.addCustomRef(this._refHeader.current, numberOursBc);
        }
    };

    async onAfterLoad(): Promise<void> {
        const { storage } = this.props;

        const isAuditTrail = storage.formMode === FormMode.AuditTrail;

        if (!isAuditTrail) {
            const promises: Promise<unknown>[] = [];
            const isNew = storage.data.bindingContext?.isNew();

            // call it first to customize type on shared forms
            promises.push(this.typeSpecificOnAfterLoad(isNew));

            if (!storage.data.entity.NumberOurs) {
                promises.push(setMatchingNumberRange(storage, true));
            } else {
                promises.push(loadAndStoreRanges(storage));
            }

            if (isNew) {
                // init type code
                storage.setValueByPath(PrSubmissionEntity.SubmissionType, this.typeCode);
                if (this.subTypeCode) {
                    storage.setValueByPath(PrSubmissionEntity.SubmissionSubtype, this.subTypeCode);
                }
                // init Employee if defaulted (Editable Window)
                const defaultEmployee = this.props.formProps?.defaultData?.[PrSubmissionEntity.EmployeeHeader];
                if (defaultEmployee?.Id) {
                    // init with employee, field will be hidden and defaulted
                    const bc = storage.data.bindingContext
                        .navigate(this.pathToEmployee);
                    // simulate change to trigger dependent field updates
                    const change: ISmartFieldChange = {
                        bindingContext: bc,
                        value: defaultEmployee.Id,
                        additionalData: defaultEmployee,
                        triggerAdditionalTasks: true
                    };
                    this.handleChange(change);
                    // set also manually value for EmployeeHeader as it's empty for new entity
                    const employee = storage.getValue(bc);
                    storage.setValue(storage.data.bindingContext.navigate(PrSubmissionEntity.EmployeeHeader), employee);
                }
            }

            await Promise.all(promises);
        }

        return super.onAfterLoad();
    }

    onBeforeSave(data?: E): E {
        if (!data) {
            data = cloneDeep(this.props.storage.getEntity());
        }
        return data;
    }

    getAdditionalLoadPromise(args: IContextInitArgs): (Promise<unknown | void>)[] {
        return [
            loadAndStoreRanges(this.props.storage),
            ...super.getAdditionalLoadPromise(args)
        ];
    }

    async handleCustomHeaderAction(actionId: string): Promise<void> {
        switch (actionId) {
            case EXPORT_XML_ACTION_ID:
                const { storage } = this.props;
                const id = storage.data.bindingContext.getKey();
                storage.setBusy(true);
                await exportToXML(storage, [id as number]);
                storage.reload({ preserveInfos: true });
            break;
        }
    }

    handleSubmitButtonPress = (): void => {
        const { storage } = this.props;
        if (storage.data.entity.DateSubmitted) {
            this.updateDateSubmitted(null);
        } else {
            storage.setCustomData({ isSubmitDialogOpen: true });
        }
        this.forceUpdate();
    };

    handleNumberRange = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getPath() === PrSubmissionEntity.NumberRange) {
            this.entity.NumberRange = e.value ? e.additionalData : null;
        }
    };

    handleChange(e: ISmartFieldChange): void {
        this.handleNumberRange(e);
        super.handleChange(e);
    }

    renderFooter = (): React.ReactElement => {
        if (!this.props.buttons) {
            return super.renderFooter();
        }

        return (
            <ButtonGroup align={TextAlign.Right}>
                {this.props.buttons}
            </ButtonGroup>
        );
    };

    handleSubmitDialogClose = (): void => {
        this.props.storage.setCustomData({ isSubmitDialogOpen: false });
        this.forceUpdate();
    }

    updateDateSubmitted = async (dateSubmitted: Date): Promise<void> => {
        // set SubmissionDate and reload storage
        const { storage } = this.props;
        const { bindingContext } = storage.data;
        const es = bindingContext.getEntitySet().getName() as EntitySetName;
        storage.setBusy(true);
        await storage.oData.getEntitySetWrapper(es)
            .update(bindingContext.getKey(), { DateSubmitted: dateSubmitted });
        this.props.onTableRefreshNeeded();
        await storage.reload({ preserveInfos: true });
    }

    handleSubmitDialogConfirm = async (dateSubmitted: Date): Promise<void> => {
        this.handleSubmitDialogClose();
        this.updateDateSubmitted(dateSubmitted);
    };

    renderSubmitDialog(): React.ReactElement {
        const { storage } = this.props;
        const { isSubmitDialogOpen } = storage.getCustomData();

        if (!isSubmitDialogOpen) {
            return null;
        }

        const id = storage.data.bindingContext.getKey() as number;

        return (
            <ExportSubmissionDialog ids={[id]}
                                    storage={storage}
                                    onConfirm={this.handleSubmitDialogConfirm}
                                    onClose={this.handleSubmitDialogClose}/>
        );
    }

    renderCustomDialogs(): React.ReactElement {
        return (<>
            {super.renderCustomDialogs()}
            {this.renderSubmitDialog()}
        </>);
    }

    renderButtons(): React.ReactElement {
        const storage = this.props.storage;
        const isFormReadOnly = storage.isReadOnly;

        const isSubmitted = !!storage.data.entity.DateSubmitted;
        const isNew = storage.data.bindingContext?.isNew();

        return (
            <ButtonGroup wrap={"wrap"}>
                {isSubmitted ? (
                    <Button hotspotId={"cancelSubmission"}
                            onClick={this.handleSubmitButtonPress}
                            isTransparent>{storage.t("Submission:CancelSubmission")}</Button>
                ) : (<>
                    <Button hotspotId={"formCancel"}
                            isDisabled={storage.isDisabled || this.shouldDisableCancelButton()}
                            onClick={this.handleFormCancel}
                            isTransparent>{storage.t("Common:General.Cancel")}</Button>
                    {this.isDeletable &&
                        <SmartFormDeleteButton storage={storage}
                                               onClick={this.handleDelete}/>
                    }
                    {this.shouldAllowCopy() && <Button hotspotId={"formCopy"}
                                                       onClick={this.handleCopy}
                                                       isDisabled={this.shouldDisableCopyButton()}
                                                       isTransparent>{storage.t("Common:Form.CreateCopy")}</Button>}
                    {!isNew && (
                        <Button hotspotId={"submitButton"}
                                onClick={this.handleSubmitButtonPress}
                                isTransparent>{storage.t("Submission:Submit")}</Button>
                    )}
                    {!isFormReadOnly &&
                        <Button hotspotId={"formSave"}
                                onClick={this.handleSaveClick}
                                isDisabled={this.shouldDisableSaveButton()}>
                            {storage.t("Common:General.Save")}
                        </Button>
                    }
                </>)}
            </ButtonGroup>
        );
    }
}

export { SubmissionsFormView as SubmissionsFormViewForExtend };